import { localDoInstante } from '@vestigo/core/area-agenda';

/**
 * ── A CHAVE DO DIA, SOZINHA — E É POR ISSO QUE ELA MORA AQUI ─────────────────
 *
 * Três funções e uma constante, tiradas de `grade-do-mes.ts` em 25/09/2026 por
 * uma razão que não é de gosto: **`peso-do-encontro.ts` precisava de duas delas,
 * e `peso-do-encontro.ts` está no pedaço de ENTRADA.**
 *
 * ## O que se mediu, e o que estava a acontecer
 *
 * A faixa do próximo encontro (`PortasDaArea`, `import` estático em `App.tsx`)
 * chama `chamadaDoTempo`, que chamava `diaDoInstante` e `somarDias` de
 * `grade-do-mes.js`. Duas funções de três linhas — mas o empacotador não move
 * funções, move **módulos**: `grade-do-mes.ts` passava a ser alcançável da
 * entrada e ia inteiro para lá, com os doze nomes de mês por extenso, os doze
 * curtos, os sete dias da semana, as seis linhas da grade, `semanasDoMes`,
 * `celulasDoMes`, `contarPorMes` e `mesMaisProximoComEncontro`.
 *
 * Medido no artefacto: **2.351 B** atribuídos a `grade-do-mes.ts` dentro de
 * `index-*.js`, e `grep janeiro` dava 2 no pedaço de entrada e **0** no pedaço da
 * `Agenda` — o calendário inteiro estava na porta, e a tela que o usa não o
 * tinha. Ver `comunidade/o-editor-nao-entra-na-porta.test.ts`, que é onde este
 * orçamento se lê.
 *
 * ## A regra que isto deixa escrita
 *
 * *Um módulo que a entrada toca é um módulo que a entrada carrega inteiro.* Se
 * uma peça da porta precisa de duas funções de um ficheiro de trinta, as duas
 * funções mudam de casa — não se importa o ficheiro de trinta.
 *
 * `grade-do-mes.ts` continua a reexportar o que já reexportava, e as cercas dela
 * continuam a importar de lá: a mudança de casa não trocou uma linha de conta, e
 * é isso que elas provam.
 *
 * ## A conta é em UTC, e isso não é descuido de fuso
 *
 * É aritmética de **calendário** — «que dia vem depois de 31/12» —, e a grelha é
 * a mesma em qualquer fuso. O construtor local (`new Date(2026, 8, 1)`) salta um
 * dia na madrugada em que o relógio muda. O fuso entra numa fronteira só:
 * `diaDoInstante`, que converte o instante do encontro para a chave do dia de
 * quem lê.
 */

/** `AAAA-MM-DD` — o dia, no fuso de quem lê. */
export type ChaveDeDia = string;

export const UM_DIA = 86_400_000;

export function utcDoDia(dia: ChaveDeDia): number {
  return Date.UTC(Number(dia.slice(0, 4)), Number(dia.slice(5, 7)) - 1, Number(dia.slice(8, 10)));
}

export function chaveDoUtc(ms: number): ChaveDeDia {
  return new Date(ms).toISOString().slice(0, 10);
}

/** O dia, no fuso de quem lê — a única fronteira em que o fuso entra. */
export function diaDoInstante(instante: Date, fuso: string): ChaveDeDia {
  return localDoInstante(instante, fuso).slice(0, 10);
}

/**
 * Soma dias a um dia — é por aqui que as setas do teclado andam na grade, e é
 * como a faixa da entrada da área sabe dizer «amanhã».
 *
 * Aritmética de calendário em UTC, como todo o resto deste ficheiro: somar 1 a
 * `2026-10-17` com o construtor local devolveria o mesmo dia em metade do
 * planeta na madrugada em que o relógio muda.
 */
export function somarDias(dia: ChaveDeDia, passo: number): ChaveDeDia {
  return chaveDoUtc(utcDoDia(dia) + passo * UM_DIA);
}
